// Copyright (c) Docugami, Inc. All rights reserved.

import { IResources, ResourceDependencies } from "sdv/Resources";
import { rangesOverlap } from "utils/MathUtils";
import { ISystem, Queries } from "../engine/Engine";
import { ComponentId } from "sdv/engine/Component";
import { ViewComponent } from "sdv/components/ViewComponent";

const deps: ResourceDependencies = ["viewPosition", "viewport", "documentId", "pages"]

/**
 * Calculates the pages that are currently visible based on the current view
 */
export class VisiblePagesSystem implements ISystem {
    get updateEnabled() { return true; }

    resources!: IResources;

    queries: Queries = {
        view: { types: [ComponentId.View] },
    }

    update() {
        const visiblePages = this.resources.get("visiblePages").value;
        if (!this.resources.check(deps)) {
            return;
        }

        const view = this.queries.view.query!.single()?.getComponent<ViewComponent>(ComponentId.View);
        if (!view) {
            return;
        }

        const pages = this.resources.get("pages").value;

        const viewTop = view.viewport.worldPosition.y;
        const viewBottom = viewTop + view.viewport.worldHeight;

        const pagesChanged = this.resources.check("pages");
        if (visiblePages.length > 0 && !pagesChanged) {
            const currentVisibleTop = visiblePages[0].position.y;
            if (currentVisibleTop <= viewTop) {
                const lastPage = visiblePages[visiblePages.length -1];
                const currentVisibleBottom = lastPage.position.y + lastPage.height;

                if (currentVisibleBottom >= viewBottom) {
                    // The last visible page encapsulates the view so there is nothing to do
                    return;
                }
            }
        }

        const newVisiblePages = [];
        let updated = false;
        for (const page of pages) {
            const pageTop = page.position.y;
            const pageBottom = pageTop + page.height;

            if (rangesOverlap(pageTop, pageBottom, viewTop, viewBottom)) {
                newVisiblePages.push(page);
                updated = visiblePages[newVisiblePages.length - 1]?.pageIndex !== page.pageIndex;
            }
            else if (pageTop > viewBottom) {
                break;
            }
        }

        if (updated || newVisiblePages.length !== visiblePages.length) {
            this.resources.set("visiblePages", newVisiblePages);
            this.resources.set("pagesLoaded", false);
        }
    }
}
